import { Refresh, Send } from '@mui/icons-material'
import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Box,
  Button,
  Card,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import React, { useState } from 'react'
import { useMutation } from 'react-query'
import {
  formatAmountInCents,
  getGuestAdoptionError,
  getGuestCheckoutUrl,
  GuestAdoptionError,
  isGuestCheckoutExpired,
} from '../../../domain/Adopters/GuestAdoption'
import {
  AdoptionAttemptGuestCheckoutDto,
  GuestAdoptionLinkDto,
  GuestAdoptionsClient,
} from '../../../interactors/clients/GuestAdoptionsClient'
import { useAdoptersStore } from '../../../store/AdoptersStore'
import { useGlobalSnackbarStore } from '../../../store/GlobalSnackBarStore'
import { GuestAdoptionChip } from './GuestAdoptionChip'
import { GuestAdoptionErrorAlert } from './GuestAdoptionErrorAlert'
import { GuestCheckoutLink } from './GuestCheckoutLink'

interface Props {
  adopterId: string
  attemptId: string
  guestCheckout: AdoptionAttemptGuestCheckoutDto
  onLinkRefreshed: (link: GuestAdoptionLinkDto) => void
  disabled?: boolean
}

export const GuestAdoptionCard: React.FC<Props> = ({
  adopterId,
  attemptId,
  guestCheckout,
  onLinkRefreshed,
  disabled = false,
}) => {
  const adoptersStore = useAdoptersStore()
  const globalSnackBarStore = useGlobalSnackbarStore()
  const [refreshLinkDialogOpen, setRefreshLinkDialogOpen] = useState(false)
  const [sendEmailToAdopter, setSendEmailToAdopter] = useState(true)
  const [error, setError] = useState<GuestAdoptionError | null>(null)
  const [isCheckingPayment, setIsCheckingPayment] = useState(false)

  const isPaid = !!guestCheckout.paidAt
  const isExpired = isGuestCheckoutExpired(guestCheckout)
  const donationInCents = guestCheckout.donationInCents ?? 0

  const refreshLinkMutation = useMutation(
    async () => await GuestAdoptionsClient.refreshGuestAdoptionLink(adopterId, attemptId, { sendEmailToAdopter }),
    {
      onSuccess: (link) => {
        onLinkRefreshed(link)
        setRefreshLinkDialogOpen(false)
        globalSnackBarStore.triggerSuccessMessage(
          link.emailSent
            ? `Un nouveau lien de paiement a été envoyé par email à l'adoptant.`
            : `Un nouveau lien de paiement a été généré.`
        )
      },
      onError: (error: unknown) => {
        setError(getGuestAdoptionError(error))
      },
    }
  )

  const openRefreshLinkDialog = () => {
    setError(null)
    setSendEmailToAdopter(true)
    setRefreshLinkDialogOpen(true)
  }

  const checkPayment = async () => {
    setIsCheckingPayment(true)
    await adoptersStore.fetchOrRefreshAdopters().catch(() => undefined)
    setIsCheckingPayment(false)
  }

  return (
    <Card sx={{ width: '100%' }}>
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1 }}>
          <Typography variant="h4" component="p" sx={{ fontSize: { xs: 18, sm: 20 }, fontWeight: 700 }}>
            {isPaid ? 'Paiement reçu' : 'En attente de paiement'}
          </Typography>
          <GuestAdoptionChip />
        </Box>

        {isPaid ? (
          <>
            <Alert severity="success">
              {`L'adoptant a réglé les frais d'adoption en ligne le ${dayjs(guestCheckout.paidAt).format(
                'DD/MM/YYYY'
              )} : l'adoption est terminée.`}
            </Alert>
            <Typography variant="body2">
              {`Montant perçu par votre association : `}
              <b>{formatAmountInCents(guestCheckout.amountInCents)}</b>
              {`, soit la totalité des frais d'adoption.`}
            </Typography>
            {donationInCents > 0 && (
              <Typography variant="body2">
                {`L'adoptant a ajouté un don de ${formatAmountInCents(
                  donationInCents
                )} à Petso. Ce don s'ajoute aux frais d'adoption : il n'est pas déduit du montant que vous percevez.`}
              </Typography>
            )}
            <Typography variant="body2" fontStyle="italic">
              La fiche de l&apos;animal n&apos;est pas modifiée automatiquement : pensez à la mettre à jour (statut,
              date de sortie, adoptant).
            </Typography>
          </>
        ) : (
          <>
            <Typography variant="body2">
              {`L'adoptant doit régler `}
              <b>{formatAmountInCents(guestCheckout.amountInCents)}</b>
              {` depuis le lien ci-dessous, sans compte ni document à signer. La procédure se clôturera automatiquement à la réception du paiement.`}
            </Typography>
            {isExpired ? (
              <Alert severity="warning">
                {`Ce lien a expiré le ${dayjs(guestCheckout.expiresAt).format(
                  'DD/MM/YYYY'
                )} : renvoyez un nouveau lien à l'adoptant.`}
              </Alert>
            ) : (
              <>
                <GuestCheckoutLink checkoutUrl={getGuestCheckoutUrl(guestCheckout.token)} />
                <Typography variant="body2" color="text.secondary">
                  {`Lien valable jusqu'au ${dayjs(guestCheckout.expiresAt).format('DD/MM/YYYY')}.`}
                </Typography>
              </>
            )}
            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 1 }}>
              <Button
                variant="outlined"
                startIcon={<Send />}
                onClick={openRefreshLinkDialog}
                disabled={disabled}
                sx={{ textTransform: 'none' }}
              >
                Renvoyer le lien
              </Button>
              <LoadingButton
                variant="text"
                startIcon={<Refresh />}
                onClick={checkPayment}
                loading={isCheckingPayment}
                sx={{ textTransform: 'none' }}
              >
                Vérifier le paiement
              </LoadingButton>
            </Box>
          </>
        )}
      </Box>

      <Dialog
        open={refreshLinkDialogOpen}
        onClose={() => setRefreshLinkDialogOpen(false)}
        PaperProps={{ style: { borderRadius: 15, padding: 15 } }}
      >
        <DialogTitle sx={{ fontSize: 20, fontWeight: 700 }}>Renvoyer le lien de paiement</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Un nouveau lien de paiement va être généré : l&apos;ancien lien ne fonctionnera plus.
          </Typography>
          <FormControlLabel
            control={
              <Checkbox
                checked={sendEmailToAdopter}
                onChange={(event) => setSendEmailToAdopter(event.target.checked)}
              />
            }
            label="Envoyer le nouveau lien par email à l'adoptant"
            sx={{ mt: 1 }}
          />
          {error && (
            <Box sx={{ mt: 1 }}>
              <GuestAdoptionErrorAlert error={error} />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Button variant="outlined" onClick={() => setRefreshLinkDialogOpen(false)} sx={{ textTransform: 'none' }}>
            Annuler
          </Button>
          <LoadingButton
            variant="contained"
            onClick={() => {
              setError(null)
              refreshLinkMutation.mutate()
            }}
            loading={refreshLinkMutation.isLoading}
            sx={{ textTransform: 'none' }}
          >
            Renvoyer le lien
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </Card>
  )
}
